import { useEffect, useState } from "react";
import operatorApiService from "../../../services/operator-api.service";
import "../CommonDesign.scss";
import editIcon from "../../../assets/imgs/master/edit_icon_wh.svg";
import deleteIcon from "../../../assets/imgs/master/delete_icon_wh.svg";

interface EditableMessage {
  message_id: number | null;
  message_word: string;
  message_type: number;
  status: "original" | "edited" | "new" | "deleted";
  isEditing?: boolean;
  internalId: string;
}

const ScreenMessage = () => {
  const [originalRows, setOriginalRows] = useState<EditableMessage[]>([]);
  const [rows, setRows] = useState<EditableMessage[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchMessages = async () => {
      const res = await operatorApiService.doGetScreenMessages();
      const initial = res.map((msg: any) => ({
        ...msg,
        status: "original",
        isEditing: false,
        internalId: `existing-${msg.message_id}`,
      }));
      setOriginalRows(initial);
      setRows(initial);
    };

    fetchMessages();
  }, []);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        message_id: null,
        message_word: "",
        message_type: 0,
        status: "new",
        isEditing: true,
        internalId: `new-${Date.now()}-${Math.random()}`,
      },
    ]);
  };

  const markDeleted = (internalId: string) => {
    setRows((prev) =>
      prev
        .filter((r) => {
          if (r.internalId !== internalId) return true;
          return r.status !== "new";
        })
        .map((r) =>
          r.internalId === internalId && r.status !== "new"
            ? { ...r, status: "deleted" }
            : r
        )
    );
  };

  const toggleEdit = (internalId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.internalId === internalId
          ? { ...r, isEditing: true, status: "edited" }
          : r
      )
    );
  };

  const handleChange = (
    index: number,
    field: keyof EditableMessage,
    value: any
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;
    setRows(updated);
  };

  const submit = async () => {
    try {
      const hasBlank = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          r.message_type === null
      );

      if (hasBlank) {
        setErrorMessage("空欄があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasTooLong = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          r.message_word.trim().length > 25
      );

      if (hasTooLong) {
        setErrorMessage("文字数が制限を超えています。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const create = rows
        .filter((r) => r.status === "new")
        .map(({ message_word, message_type }) => ({
          message_word,
          message_type,
        }));
      const update = rows
        .filter((r) => r.status === "edited")
        .map(({ message_id, message_word, message_type }) => ({
          message_id,
          message_word,
          message_type,
        }));
      const del = rows
        .filter((r) => r.status === "deleted")
        .map((r) => r.message_id);

      if (create.length === 0 && update.length === 0 && del.length === 0) {
        return;
      }

      const response = await operatorApiService.saveScreenMessages({
        create,
        update,
        delete: del,
      });

      if (response.status === "success") {
        setSuccessMessage("更新に成功しました。");
        setTimeout(() => setSuccessMessage(null), 3000);

        const refreshed = await operatorApiService.doGetScreenMessages();
        const mapped = refreshed.map((msg: any) => ({
          ...msg,
          status: "original",
          isEditing: false,
          internalId: `existing-${msg.message_id}`,
        }));
        setRows(mapped);
        setOriginalRows(mapped);
      }
    } catch (error) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleResetAll = () => {
    const reset = originalRows.map((msg) => ({ ...msg }));
    setRows(reset);
  };

  const getMessageTypeLabel = (type: number) => {
    switch (type) {
      case 0:
        return "文字のみ";
      case 1:
        return "警告";
      case 2:
        return "OK/Cancel";
      default:
        return "-";
    }
  };

  return (
    <div className="screenMessage">
      <div className="content">
        <div className="title">メッセージ</div>
        <div className="section">
          <div className="sectionTitle">メッセージ一覧</div>
          <div className="sectionBody">
            <table className="tableArea">
              <thead>
                <tr>
                  <th>メッセージID</th>
                  <th>メッセージ文言</th>
                  <th>メッセージ種別</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.internalId}>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.message_id ?? "新規"}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.message_word}
                          onChange={(e) =>
                            handleChange(i, "message_word", e.target.value)
                          }
                        />
                      ) : (
                        row.message_word
                      )}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <select
                          value={row.message_type}
                          onChange={(e) =>
                            handleChange(
                              i,
                              "message_type",
                              parseInt(e.target.value)
                            )
                          }
                        >
                          <option value={0}>0:文字のみ</option>
                          <option value={1}>1:警告</option>
                          <option value={2}>2:OK/Cancel</option>
                        </select>
                      ) : (
                        getMessageTypeLabel(row.message_type)
                      )}
                    </td>
                    <td>
                      {row.status === "deleted" ? (
                        <span className="deletionLabel">削除予定</span>
                      ) : row.status === "edited" ? (
                        <span className="editLabel">編集予定</span>
                      ) : row.status === "new" ? (
                        <button
                          className="cancelButton"
                          onClick={() => markDeleted(row.internalId)}
                        >
                          取消
                        </button>
                      ) : (
                        <div className="buttonGroup">
                          <button
                            className="updateButton"
                            onClick={() => toggleEdit(row.internalId)}
                          >
                            <img src={editIcon} alt="" className="icon" />
                            編集
                          </button>
                          <button
                            className="deleteButton"
                            onClick={() => markDeleted(row.internalId)}
                          >
                            <img src={deleteIcon} alt="" className="icon" />
                            削除
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="createFooter">
              <div className="createLabel">
                表に新規入力欄が追加されます。
                新規メッセージを入力し、登録してください。
              </div>
              <button className="createButton" onClick={addRow}>
                新規行追加
              </button>
            </div>
            {rows.some((r) => r.status !== "original") && (
              <div className="actionButtons">
                <button className="backButton" onClick={handleResetAll}>
                  戻る
                </button>
                <button className="decisionButton" onClick={submit}>
                  確定
                </button>
              </div>
            )}
            {successMessage && (
              <div className="successMessage">{successMessage}</div>
            )}
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenMessage;
